import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import {
    AGUAdPlacement,
    AGUAdSessionService,
    AGUAppLocation,
    AGUAppModuleAccent,
    AGUAppModuleDefinition,
    AGUIconName,
    AGUToastService,
    AGUViewportService,
    FeatureRotation,
    GreetingFor,
    HomeGreeting,
    HomeGreetingInput,
    IsModuleReentry,
    IsRowTap,
    SidebarNavLabel,
} from 'agu_app_shell';
import { ExplorerRaHostAdapter } from '../../ra-explorer-host.adapter';
import { RaCompositionState } from '../../ra-composition.state';
import { RA_MODULE_KEYS } from '../../ra-module-keys';
import { ResearchApiService } from '../../services/research-api.service';
import { MemberItemStateChange, MemberItemStateService } from '../../services/member-item-state.service';
import { ItemKind, ResearchHome, ResearchRecommendations } from '../../models/research.models';
import { FormatAwardLine } from '../grant-finder/grant-card.component';
import { PersonRoleLine, PersonTagChip } from '../people-finder/person-card.component';
import { FormatJobMeta } from '../job-finder/job-card.component';
import { DescribeError } from '../shared/describe-error';
import { RECOMMENDED_COUNT } from '../shared/recommended-count';
import { LoadFailures } from '../shared/load-failures';
import { PortalConfigService } from '../../services/portal-config.service';
import { MembershipService, ShowCvModule } from '../../services/membership.service';
import {
    HOME_CAUGHT_UP_COPY,
    HomePartialFailure,
    HomePartialFailureCopy,
    HomeTileKind,
    NOT_REFRESHED_PILL,
    PageErrorCopy,
    REFRESHING_MATCHES_SUB,
    StatePanelCopy,
} from '../shared/state-copy';

/**
 * The Home feature on show (P/Member_Hub.html:1515-1517): the creative at `index` among those sharing the first one's `rotate`
 * key — the session's dismissals are already out of `pool`, as the prototype filters hidden ids before it takes `n % length`.
 */
export function PickHomeFeature(pool: readonly AGUAdPlacement[], index: number): string | null {
    const key = pool[0]?.rotate ?? null;
    return FeatureRotation(pool.filter((p) => p.rotate === key).map((p) => p.id), index);
}

/** Views this module has. Narrows AGUAppLocation for every internal comparison. */
export type RaDashboardView = 'home';

/** Career Explorer's fallback location for the CV callout, for the case where the module is not in this composition. `'start'` is its `DefaultView`. */
const CAREER_MAP_FALLBACK_LOCATION: AGUAppLocation = { ModuleKey: RA_MODULE_KEYS.CareerMap, View: 'start' };

/** Saved's `DefaultView`, for a composition without the Saved module (mirrors `CAREER_MAP_FALLBACK_LOCATION`). */
const SAVED_FALLBACK_VIEW = 'board';

/** The placeholder `AGUAppModuleHomeCopy.Secondary` carries when its label counts recommendations. Its presence is also what marks the action as "opens the Recommended tab". */
const RECOMMENDED_PLACEHOLDER = '{N}';

/** Where a pick row's "Open" action navigates, per item kind — mirrors each finder page's own `OnOpen` (people opens on `'profile'`, grants/jobs on `'detail'`). */
const PICK_OPEN_TARGET: Readonly<Record<ItemKind, { moduleKey: string; view: string }>> = {
    grant: { moduleKey: RA_MODULE_KEYS.Grants, view: 'detail' },
    person: { moduleKey: RA_MODULE_KEYS.People, view: 'profile' },
    job: { moduleKey: RA_MODULE_KEYS.Jobs, view: 'detail' },
};

const PICK_KIND_LABEL: Readonly<Record<ItemKind, string>> = {
    grant: 'Grant',
    person: 'Person', // P/Member_Hub.html:1095
    job: 'Job',
};

/** Which module owns each kind of pick. The row then borrows that module's own colour — see `PickAccents`. */
export const PICK_KIND_MODULE: Readonly<Record<ItemKind, string>> = {
    grant: RA_MODULE_KEYS.Grants,
    person: RA_MODULE_KEYS.People,
    job: RA_MODULE_KEYS.Jobs,
};

/**
 * A pick row's colour rail, kind label, "Open" link and hover ground, resolved
 * from the module that owns the kind rather than restated here.
 *
 * Nothing to keep in step: re-accenting Jobs in `RA_MODULE_DEFINITIONS` turns
 * the Jobs tile AND every job pick rail in one edit. `accentOf` is
 * `RaCompositionState.AccentOf`, passed in rather than imported — the module
 * table imports every page component, so importing it back from a page would
 * close an evaluation cycle (see that method's own note).
 */
export function PickAccents(accentOf: (moduleKey: string) => AGUAppModuleAccent): Readonly<Record<ItemKind, AGUAppModuleAccent>> {
    return {
        grant: accentOf(PICK_KIND_MODULE.grant),
        person: accentOf(PICK_KIND_MODULE.person),
        job: accentOf(PICK_KIND_MODULE.job),
    };
}

/** One row of "This week's picks" — the top pick per kind, with its meta line pre-formatted at load time (never a template getter; see `RaDashboardComponent` doc). */
export interface HomePickRow {
    kind: ItemKind;
    key: string;
    kindLabel: string;
    /** Drives `[data-accent]`, which the stylesheet turns into the row's `--accent` cascade. */
    accent: AGUAppModuleAccent;
    title: string;
    meta: string;
    saved: boolean;
}

/** One "Your research dashboard" tile — a sibling module that opted in with `HomeTile`, plus the pill its live counts produce. */
export interface HomeTileCard {
    module: AGUAppModuleDefinition;
    /** The module's own colour, straight from its definition — the tile's `[data-accent]`. */
    accent: AGUAppModuleAccent;
    /** The tile heading. `NavLabel ?? Title`, so the tile reads "Grants" beside a rail that also reads "Grants" rather than the longer page title "Grant Finder". */
    title: string;
    pillText: string | null;
    /** 'accent' tints the pill in the module colour (a new-match count); 'done' is the green "CV added" chip (P/Member_Hub.html:361); 'pending' is the neutral "No CV yet"; 'failed' the error-toned "Not refreshed" (P/proto-states.js:112-113). */
    pillVariant: HomeTilePillVariant;
    /** The tile's sentence — `HomeCopy.Body`, falling back to the module's generic `Description`. */
    body: string;
    primaryLabel: string;
    /** Leading glyph on the primary CTA — `HomeCopy.PrimaryIcon`, or null for a module with no HomeCopy (plain "Open"). */
    primaryIcon: AGUIconName | null;
    secondaryLabel: string | null;
    /** The view the primary action opens (the module's `DefaultView` unless the tile continues somewhere deeper, as Career Explorer's does once a CV is on file). */
    primaryView: string;
    /** The view a non-Recommended secondary opens; null when there is no secondary. */
    secondaryView: string | null;
    /**
     * true: the secondary link opens the module's Recommended tab (`RecordID: 'recommended'`).
     * false: it repeats the primary action (Career Map has no Recommended tab).
     */
    secondaryIsRecommended: boolean;
}

export type HomeTilePillVariant = 'accent' | 'done' | 'pending' | 'failed';

/**
 * The tile pill — a live count, or the CV state for Career Map.
 *
 * This is the one place a module KEY still decides something on Home, and
 * deliberately so: `HomeCopy` covers the words, but which number belongs on
 * which tile is a property of `ResearchHome`'s own shape (`newGrants`,
 * `newPeople`, `newJobs`, `hasCV`), not something a module can declare. A
 * module this table doesn't know gets no pill rather than a wrong one.
 */
function NewPill(count: number): { text: string | null; variant: HomeTilePillVariant } {
    return count > 0 ? { text: `${count} new`, variant: 'accent' } : { text: null, variant: 'accent' };
}

function PillFor(module: AGUAppModuleDefinition, home: ResearchHome): { text: string | null; variant: HomeTilePillVariant } {
    switch (module.Key) {
        case RA_MODULE_KEYS.Grants:
            return NewPill(home.newGrants);
        case RA_MODULE_KEYS.People:
            return NewPill(home.newPeople);
        case RA_MODULE_KEYS.Jobs:
            return NewPill(home.newJobs);
        case RA_MODULE_KEYS.CareerMap:
            return home.hasCV ? { text: 'CV added', variant: 'done' } : { text: 'No CV yet', variant: 'pending' };
        default:
            return { text: null, variant: 'accent' };
    }
}

/**
 * Career Explorer's tile once a CV is on file (spec F6): it opens the paths ("See your career paths"), plus "Continue: {role}" when a top role is stored. With no stored matches
 * (parse-only today) there is no secondary: the ready page explains itself and its Sharpen card invites tag review (spec F6, line 214).
 * Without a CV the tile keeps its `HomeCopy` ("Upload your CV" / "Start your career journey", both to `start`).
 */
function CareerTileAfterCv(home: ResearchHome): { primaryLabel: string; secondaryLabel: string | null; primaryView: string; secondaryView: string | null } {
    return { primaryLabel: 'See your career paths', secondaryLabel: home.careerTopRole ? `Continue: ${home.careerTopRole}` : null, primaryView: 'paths', secondaryView: home.careerTopRole ? 'paths' : null };
}

/**
 * The tile's `{N}` — that module's recommendation count CAPPED at `RECOMMENDED_COUNT`, the most its own
 * Recommended tab ever loads (so the tile says the number the tab lists), read the same way `PillFor` reads `home.new*`. Not `home.picks.<kind>.length`: `picks` is
 * Home's own top-3-per-kind slice for "This week's picks", a different, smaller number (fix round 2,
 * 2026-09-29 — see `ResearchHome.recommended*` on the server).
 */
function RecommendedCountFor(module: AGUAppModuleDefinition, home: ResearchHome): number {
    switch (module.Key) {
        case RA_MODULE_KEYS.Grants:
            return Math.min(home.recommendedGrants, RECOMMENDED_COUNT);
        case RA_MODULE_KEYS.People:
            return Math.min(home.recommendedPeople, RECOMMENDED_COUNT);
        case RA_MODULE_KEYS.Jobs:
            return Math.min(home.recommendedJobs, RECOMMENDED_COUNT);
        default:
            return 0;
    }
}

/**
 * Home's tiles, built from module METADATA rather than a switch statement:
 * `HomeTile` says which siblings get one, `Accent` says what colour it is, and
 * `HomeCopy` supplies all three strings. That is what makes the band work for
 * any composition — adding a fifth finder is a `ra-module-definitions.ts` edit,
 * not a Home edit.
 *
 * `{N}` in `HomeCopy.Secondary` is substituted with that module's real
 * recommendation count (`RecommendedCountFor` — `min(home.recommended*, RECOMMENDED_COUNT)`,
 * the number that finder's own Recommended tab lists when the member clicks through; fix round 2, 2026-09-29 — this used to be a shared constant
 * that didn't match). Its PRESENCE is also what marks the secondary action as
 * "opens the Recommended tab" — Career Map's secondary ("Start your career
 * journey") counts nothing and has no Recommended tab to open, and the two
 * facts are the same fact. A count of exactly 0 drops the secondary link
 * entirely (`secondaryLabel: null`) rather than promising an empty tab.
 *
 * A module that declares `HomeTile` but no `HomeCopy` (a future finder added
 * before this file's copy is written) still renders — its generic `Description`
 * and a plain "Open" — rather than blanking the tile.
 */
export function BuildTileCards(modules: readonly AGUAppModuleDefinition[], home: ResearchHome): HomeTileCard[] {
    return modules
        .filter((module) => module.HomeTile)
        .map((module) => {
            const copy = module.HomeCopy;
            const secondary = copy?.Secondary ?? null;
            const pill = PillFor(module, home);
            const secondaryIsRecommended = secondary !== null && secondary.includes(RECOMMENDED_PLACEHOLDER);
            const recommendedCount = secondaryIsRecommended ? RecommendedCountFor(module, home) : 0;
            const secondaryLabel =
                secondary === null
                    ? null
                    : secondaryIsRecommended && recommendedCount === 0
                        ? null
                        : secondary.replace(RECOMMENDED_PLACEHOLDER, String(recommendedCount));
            const career = module.Key === RA_MODULE_KEYS.CareerMap && home.hasCV ? CareerTileAfterCv(home) : null;
            return {
                module,
                accent: module.Accent,
                title: SidebarNavLabel(module),
                pillText: pill.text,
                pillVariant: pill.variant,
                body: copy?.Body ?? module.Description,
                primaryLabel: career?.primaryLabel ?? copy?.Primary ?? 'Open',
                primaryIcon: career ? null : copy?.PrimaryIcon ?? null,
                secondaryLabel: career ? career.secondaryLabel : secondaryLabel,
                primaryView: career?.primaryView ?? module.DefaultView,
                secondaryView: career ? career.secondaryView : secondaryLabel === null ? null : module.DefaultView,
                secondaryIsRecommended,
            };
        });
}

/**
 * Whether an item-state change that touched no visible pick row should re-load Home.
 *
 * Only the CONFIRMED emission of a non-dismiss change, and only while Home is the module on
 * screen. `MemberItemStateService.Toggle` emits twice per toggle (optimistic, then the server's
 * truth), and Home stays mounted while the member works in a finder, so the old "any non-dismiss
 * change" rule re-fetched Home twice for every save made anywhere in the app (2026-10-05 stage
 * sanity, A-J "outside the rows"). A hidden Home loses nothing by skipping: it re-loads on
 * re-entry (`OnLocationChanged`).
 */
export function ShouldReloadHomeForChange(change: MemberItemStateChange, current: AGUAppLocation): boolean {
    // Only an undone dismissal brings a pick back; a save elsewhere (a Saved-board Undo) never reloads Home (F-11).
    return change.confirmed && change.undismissed && !change.itemState.dismissed && current.ModuleKey === RA_MODULE_KEYS.Dashboard;
}

/**
 * homeQuiet (proto-states.js:69): on a first visit nothing is "new since" and nothing is matched
 * yet worth a count, so the tiles drop their "N new" pills and their "See N recommended" links.
 * The Career Explorer CV pill (`done`/`pending`) stays — the prototype's `:not(#cvPill)`.
 */
export function QuietTiles(cards: readonly HomeTileCard[]): HomeTileCard[] {
    return cards.map((card) => ({
        ...card,
        pillText: card.pillVariant === 'accent' ? null : card.pillText,
        secondaryLabel: card.secondaryIsRecommended ? null : card.secondaryLabel,
    }));
}

/** The tile module that carries each item kind's counts — the reverse of `PICK_KIND_MODULE`. */
function TileKindOf(moduleKey: string): HomeTileKind | null {
    const entry = (Object.keys(PICK_KIND_MODULE) as ItemKind[]).find((kind) => PICK_KIND_MODULE[kind] === moduleKey);
    return entry ?? null;
}

/**
 * Home's partial failure (P/proto-states.js:111-113): a tile whose kind the server could not refresh (`ResearchHome.failedKinds`)
 * reads "Not refreshed" in the error tone instead of its count, and the banner names it beside the tiles that are up to date.
 */
export function MarkFailedTiles(cards: readonly HomeTileCard[], failed: readonly ItemKind[]): HomeTileCard[] {
    return cards.map((card) => {
        const kind = TileKindOf(card.module.Key);
        return kind !== null && failed.includes(kind) ? { ...card, pillText: NOT_REFRESHED_PILL, pillVariant: 'failed' } : card;
    });
}

/** The partial-failure banner's sentence for the tiles on screen, or null when none of them failed. */
export function PartialFailureFor(cards: readonly HomeTileCard[], failed: readonly ItemKind[]): HomePartialFailureCopy | null {
    const shown = cards.map((c) => TileKindOf(c.module.Key)).filter((k): k is HomeTileKind => k !== null);
    return HomePartialFailure(shown, failed);
}

/**
 * "You are all caught up" (P/proto-states.js:95): a returning visit with nothing new since the last one. Never while the member
 * cannot be matched yet (`picks.mode === 'none'` keeps "We can't match you yet") or while a kind failed to refresh (zero new
 * there is not a fact — the partial-failure banner says so instead).
 */
export function IsCaughtUp(home: ResearchHome, since: SinceCardModel | null): boolean {
    return since !== null && home.picks.mode !== 'none' && home.failedKinds.length === 0 && home.newGrants + home.newPeople + home.newJobs === 0;
}

/** Where a "Get set up" step's button goes; null for a step with nothing left to do. */
export type SetupStepAction = 'profile' | 'career-map';

/** One row of the first-visit "Get set up" card (proto-states.js:73-77). */
export interface SetupStep {
    readonly Label: string;
    readonly Body: string;
    readonly Done: boolean;
    readonly Action: SetupStepAction | null;
    /** The step's button text ("Review" / "Upload CV"), shown only while the step is not done. */
    readonly ActionLabel: string | null;
}

/**
 * The "Get set up" steps, verbatim from proto-states.js:74-76. The prototype's fourth step,
 * "Choose what you hear about" (:77), is not built: the hub has no alerts feature, so it would
 * promise something that does not exist (spec Deviations).
 */
export function SetupSteps(home: ResearchHome): ReadonlyArray<SetupStep> {
    return [
        { Label: 'Create your AGU account', Body: 'Done when you joined.', Done: true, Action: null, ActionLabel: null },
        {
            Label: 'Confirm your research areas',
            Body: 'We pre-filled three from your AGU abstracts. Check they are right.',
            Done: home.researchAreasConfirmed,
            Action: 'profile',
            ActionLabel: 'Review',
        },
        { Label: 'Add your CV', Body: 'Unlocks Career Explorer and sharper job matches.', Done: home.hasCV, Action: 'career-map', ActionLabel: 'Upload CV' },
    ];
}

/** "n of 3 done" (proto-states.js:73). */
export function SetupProgressLabel(steps: ReadonlyArray<SetupStep>): string {
    return `${steps.filter((s) => s.Done).length} of ${steps.length} done`;
}

/** A "Since your last visit" action: Review saved, or the Career Explorer "Continue: {rank-1 role}". */
export interface SinceAction {
    readonly Label: string;
    readonly Action: 'saved' | 'continue';
}

export interface SinceCardModel {
    readonly Title: string;
    readonly Text: string;
    readonly Actions: ReadonlyArray<SinceAction>;
}

/**
 * The returning visit's "Since your last visit · {date}" card (proto-states.js:88). The text
 * promises nothing the hub does not do (spec truthfulness): the new-match count, then the
 * earliest saved-grant deadline only when one exists inside 14 days. The date is the previous
 * visit's, in the short "Tue 14 Oct" form. Null on a first visit — there is no last visit.
 */
export function SinceCard(home: ResearchHome): SinceCardModel | null {
    if (home.visitCount <= 1 || home.previousVisitAt === null) return null;
    const date = new Date(home.previousVisitAt).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }).replace(',', '');
    const n = home.newGrants + home.newPeople + home.newJobs;
    const days = home.savedClosingSoonDays;
    const deadline = days === null ? '' : days === 0 ? ' One saved grant closes today.' : ` One saved grant closes in ${days} day${days === 1 ? '' : 's'}.`;
    return {
        Title: `Since your last visit · ${date}`,
        Text: `${n} new match${n === 1 ? '' : 'es'} across grants, people and jobs.${deadline}`,
        Actions: [
            { Label: 'Review saved', Action: 'saved' },
            ...(home.hasCV && home.careerTopRole ? [{ Label: `Continue: ${home.careerTopRole}`, Action: 'continue' as const }] : []),
        ],
    };
}

/** `ResearchHome` → the pure `GreetingFor` helper's input shape. */
function ToGreetingInput(home: ResearchHome): HomeGreetingInput {
    return {
        displayName: home.displayName,
        visitCount: home.visitCount,
        previousVisitAt: home.previousVisitAt,
        newMatches: home.newGrants + home.newPeople + home.newJobs,
        savedClosingSoon: home.savedClosingSoon,
        savedClosingSoonTwoWeeks: home.savedClosingSoonTwoWeeks,
        visitStreak: home.visitStreak,
        visitsThisMonth: home.visitsThisMonth,
    };
}

/** Top pick per kind from `ResearchRecommendations`, formatted with each finder card's own pure meta formatter so the numbers/wording never drift from what the finder itself shows. */
export function BuildPickRows(picks: ResearchRecommendations, accents: Readonly<Record<ItemKind, AGUAppModuleAccent>>): HomePickRow[] {
    const rows: HomePickRow[] = [];

    const grant = picks.grants[0];
    if (grant) {
        rows.push({
            kind: 'grant',
            key: grant.contentID,
            kindLabel: PICK_KIND_LABEL.grant,
            accent: accents.grant,
            title: grant.title,
            meta: [grant.grantor, FormatAwardLine(grant)].filter((v): v is string => Boolean(v)).join(' · '),
            saved: grant.itemState.saved,
        });
    }

    const person = picks.people[0];
    if (person) {
        rows.push({
            kind: 'person',
            key: person.contributorID,
            kindLabel: PICK_KIND_LABEL.person,
            accent: accents.person,
            title: person.name,
            meta: [PersonRoleLine(person), PersonTagChip(person.overlaps)].filter((v): v is string => Boolean(v)).join(' · '),
            saved: person.itemState.saved,
        });
    }

    const job = picks.jobs[0];
    if (job) {
        rows.push({
            kind: 'job',
            key: job.jobId,
            kindLabel: PICK_KIND_LABEL.job,
            accent: accents.job,
            title: job.title,
            // home.html:561 — Home's pick row prefixes the compact age: `… · posted 2d ago`.
            meta: FormatJobMeta(job, { posted: 'prefixed' }),
            saved: job.itemState.saved,
        });
    }

    return rows;
}

/**
 * The member dashboard — the composite app's landing module.
 *
 * The dashboard band's tiles are entirely derived from the composition: one
 * tile per visible sibling module that opted in with `HomeTile` (so Saved and
 * Settings stay sidebar-only), in the app's own nav order
 * (`RaCompositionState.SiblingsOf`) — that is what makes it work for any
 * composition rather than a hardcoded module count. `BuildTileCards` reads each
 * module's `Accent` and `HomeCopy` straight from `RA_MODULE_DEFINITIONS`, so
 * adding a fifth finder is a metadata edit and never a change here.
 *
 * `RecordVisit()` (which bumps `Research.MemberProfile.VisitCount`) fires at
 * most once per surface session, guarded by `RaCompositionState
 * .VisitRecorded` — switching from Grants back to Home should not look like
 * a second visit. Every other load (`Home()`) is side-effect-free.
 *
 * Every date-dependent value (`greeting`, `refreshedDate`) is computed once
 * per `LoadHome()` into a plain field, never a template getter — a getter
 * re-reading `new Date()` on every change-detection pass is the "never bind
 * time-dependent calls in templates" trap (ui-best-practices §7; the same
 * discipline `GrantCardComponent.ClosingLine` and `JobCardComponent
 * .MetaLine` already follow).
 *
 * Product states (PD §5): 3 skeleton cards on the first load and in place of the picks while Home reloads, with the dashboard
 * sub line reading "Refreshing your matches…" (P/proto-states.js:128); "You are all caught up" on a returning visit with nothing
 * new (:95); the partial-failure banner and "Not refreshed" pill (:111-113); the error panel when Home itself fails to load.
 * Truthfulness edit: "You are all caught up" drops "and email you when something fits" (:95) — the hub sends no match emails.
 */
@Component({
    standalone: false,
    selector: 'ra-dashboard',
    templateUrl: './ra-dashboard.component.html',
    styleUrl: './ra-dashboard.component.scss',
    // ra-fullwidth drops the centred 1200px reading column (styles.scss) that
    // every unported module still uses. Home's mockup has no max-width and no
    // centring: `.main` is a 1fr track on flat 32px gutters with 30px/36px of
    // block padding, and the dash band, the picks card and the CV callout are
    // all full-bleed inside it (parity/home.md §2). Same opt-in the three
    // finder pages take.
    host: { class: 'ra-fullwidth' },
})
export class RaDashboardComponent implements OnInit, OnDestroy {
    private readonly host = inject(ExplorerRaHostAdapter);
    private readonly composition = inject(RaCompositionState);
    private readonly api = inject(ResearchApiService);
    private readonly itemState = inject(MemberItemStateService);
    /** The phone shell's toast and viewport, surface-provided (P/Member_Hub.html:953-958, 1011-1019); absent outside a surface. */
    private readonly toast = inject(AGUToastService, { optional: true });
    private readonly viewport = inject(AGUViewportService, { optional: true });
    /** The Featured placements' session state, surface-provided; absent outside a surface. */
    private readonly ads = inject(AGUAdSessionService, { optional: true });
    /** The memoised portal config, for the error panel's Contact support address (PD §5); absent outside a surface. */
    private readonly portalConfig = inject(PortalConfigService, { optional: true });
    /** The surface's membership state; Home's CV module hides while lapsed (P/proto-states.js:139). */
    private readonly membership = inject(MembershipService, { optional: true });
    /** The Home feature chosen for this session (null until one is), so a dismissal leaves the slot empty instead of rotating in the next. */
    private featureId: string | null = null;

    loading = true;
    error: string | null = null;
    /** The error panel's foot, retry note and Contact support link when Home fails to load (PD §5). */
    readonly failures = new LoadFailures();
    /** Consecutive loads that came back with a tile not refreshed — the banner's retry note (P/proto-states.js:228-238). */
    readonly partialFailures = new LoadFailures();
    readonly ErrorCopy: StatePanelCopy = PageErrorCopy('your dashboard');
    readonly CaughtUpCopy: StatePanelCopy = HOME_CAUGHT_UP_COPY;
    readonly RefreshingSub = REFRESHING_MATCHES_SUB;
    /** A returning visit with zero new matches (P/proto-states.js:95). */
    caughtUp = false;
    /** The partial-failure banner's sentence, or null (P/proto-states.js:111). */
    partial: HomePartialFailureCopy | null = null;

    home: ResearchHome | null = null;
    greeting: HomeGreeting | null = null;
    /** "Wednesday, 19 August" — computed once per load alongside `greeting`. */
    refreshedDate = '';
    /**
     * Whether this composition gives Home any sibling at all. A field, not the
     * `Modules` getter, because the template branches on it — a getter there
     * re-walks the composition on every change-detection pass.
     */
    hasModules = false;

    tileCards: readonly HomeTileCard[] = [];
    pickRows: HomePickRow[] = [];
    /** First visit (proto-states.js:69-70): quiet tiles, no CV callout, and "Get set up" in place of the picks. */
    homeQuiet = false;

    /** Whether the "CV isn't on file" module renders: not for a member with a CV, on a quiet first visit, or while lapsed. */
    get ShowCvModule(): boolean { return ShowCvModule(this.home?.hasCV ?? true, this.homeQuiet, this.membership?.IsLapsed ?? false); }
    setupSteps: ReadonlyArray<SetupStep> = [];
    setupProgress = '';
    /** The setup bar's fill, as a CSS width ("33%"). */
    setupPercent = '0%';
    since: SinceCardModel | null = null;

    private stateSub: Subscription | null = null;
    private locationSub: Subscription | null = null;
    private reentrySub: Subscription | null = null;
    /**
     * Module the surface was on at the last `Location$` emission. Seeded with
     * Home's own key so the emission that arrives on subscribe — always this
     * module, since Home only mounts once it is opened — is not mistaken for a
     * re-entry and does not load a second time on top of `ngOnInit`.
     */
    private previousModuleKey: string = RA_MODULE_KEYS.Dashboard;

    /** The Featured module after the dashboard tiles (P/Member_Hub.html:1494-1522), rotating per session; null when none or dismissed. */
    get Feature(): AGUAdPlacement | null {
        const pool = this.ads?.Select('home', 'feature') ?? [];
        if (this.featureId === null && this.ads && pool.length > 0) this.featureId = PickHomeFeature(pool, this.ads.NextFeatureIndex());
        return pool.find((p) => p.id === this.featureId) ?? null;
    }

    ngOnInit(): void {
        this.stateSub = this.itemState.Changes$.subscribe((change) => this.OnItemStateChanged(change));
        this.locationSub = this.host.Location$.subscribe((loc) => this.OnLocationChanged(loc));
        this.reentrySub = this.composition.SurfaceReentered$.subscribe(() => this.OnSurfaceReentered());
        void this.failures.ReadSupport(this.portalConfig);
        void this.LoadHome();
    }

    ngOnDestroy(): void {
        this.stateSub?.unsubscribe();
        this.locationSub?.unsubscribe();
        this.reentrySub?.unsubscribe();
    }

    /**
     * The member came back to the whole surface from another app (B-3). No
     * `Location$` change fires — the surface is re-attached exactly where it
     * was — so a Home that is on screen re-loads here; the surface has already
     * reset the visit flag, so this load records the new visit. A hidden Home
     * waits for its own re-entry (`OnLocationChanged`) as before.
     */
    private OnSurfaceReentered(): void {
        if (this.host.CurrentLocation.ModuleKey === RA_MODULE_KEYS.Dashboard) void this.LoadHome();
    }

    /**
     * The surface keeps every visited module MOUNTED (see `WithMountedModule`),
     * so Home is not re-created when the member comes back to it — and its
     * counts and picks are exactly what the rest of the app changes in the
     * meantime. Re-entry therefore re-loads, which is the same freshness a
     * destroy-and-recreate surface got from `ngOnInit`. `LoadHome` still records
     * a visit only once per surface session (`RaCompositionState.VisitRecorded`),
     * so this does not inflate the member's visit count.
     */
    private OnLocationChanged(loc: AGUAppLocation): void {
        const reentered = IsModuleReentry(RA_MODULE_KEYS.Dashboard, this.previousModuleKey, loc.ModuleKey);
        this.previousModuleKey = loc.ModuleKey;
        if (reentered) void this.LoadHome();
    }

    /** Sibling modules, which are exactly the cards to render — 'dashboard' itself is never its own tile. */
    get Modules(): readonly AGUAppModuleDefinition[] {
        return this.composition.SiblingsOf(RA_MODULE_KEYS.Dashboard);
    }

    get LastDismissed$() {
        return this.itemState.LastDismissed$;
    }

    Retry(): void {
        void this.LoadHome();
    }

    // ── Load ─────────────────────────────────────────────────────────────

    private async LoadHome(): Promise<void> {
        this.loading = true;
        this.error = null;
        try {
            const home = this.composition.VisitRecorded ? await this.api.Home() : await this.RecordVisitOnce();
            this.ApplyHome(home);
            this.failures.Reset();
        } catch (e) {
            this.error = DescribeError(e);
            this.failures.Record(e);
        } finally {
            this.loading = false;
        }
    }

    private async RecordVisitOnce(): Promise<ResearchHome> {
        const home = await this.api.RecordVisit();
        this.composition.MarkVisitRecorded();
        return home;
    }

    private ApplyHome(home: ResearchHome): void {
        const now = new Date();
        this.home = home;
        this.greeting = GreetingFor(ToGreetingInput(home), now);
        this.refreshedDate = now.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
        this.hasModules = this.Modules.length > 0;
        // The greeting's own first-visit rule decides the quiet state, so the two never disagree.
        this.homeQuiet = this.greeting.tagKind === 'welcome';
        const tiles = MarkFailedTiles(BuildTileCards(this.Modules, home), home.failedKinds);
        this.tileCards = this.homeQuiet ? QuietTiles(tiles) : tiles;
        this.partial = PartialFailureFor(this.tileCards, home.failedKinds);
        if (this.partial) this.partialFailures.Record(null);
        else this.partialFailures.Reset();
        this.setupSteps = this.homeQuiet ? SetupSteps(home) : [];
        this.setupProgress = SetupProgressLabel(this.setupSteps);
        this.setupPercent = this.setupSteps.length === 0 ? '0%' : `${Math.round((this.setupSteps.filter((s) => s.Done).length / this.setupSteps.length) * 100)}%`;
        this.since = this.homeQuiet ? null : SinceCard(home);
        this.caughtUp = IsCaughtUp(home, this.since);
        this.pickRows = BuildPickRows(home.picks, PickAccents((key) => this.composition.AccentOf(key)));

        this.itemState.Prime(
            'grant',
            home.picks.grants.map((g) => ({ key: g.contentID, itemState: g.itemState }))
        );
        this.itemState.Prime(
            'person',
            home.picks.people.map((p) => ({ key: p.contributorID, itemState: p.itemState }))
        );
        this.itemState.Prime(
            'job',
            home.picks.jobs.map((j) => ({ key: j.jobId, itemState: j.itemState }))
        );
    }

    // ── Dashboard tiles ──────────────────────────────────────────────────

    OnPrimaryClick(card: HomeTileCard): void {
        this.host.NavigateTo({ ModuleKey: card.module.Key, View: card.primaryView });
    }

    OnSecondaryClick(card: HomeTileCard): void {
        if (card.secondaryIsRecommended) {
            this.host.NavigateTo({ ModuleKey: card.module.Key, View: 'search', RecordID: 'recommended' });
            return;
        }
        this.host.NavigateTo({ ModuleKey: card.module.Key, View: card.secondaryView ?? card.primaryView });
    }

    /** The first-visit greeting's "Grants"/"Collaborators"/"Jobs" mentions — navigates to that sibling if it's in this composition, else its default search view. */
    OnGreetingLinkClick(moduleKey: string): void {
        const module = this.Modules.find((m) => m.Key === moduleKey);
        this.host.NavigateTo({ ModuleKey: moduleKey, View: module?.DefaultView ?? 'search' });
    }

    // ── Get set up / Since your last visit ───────────────────────────────

    OnSetupAction(action: SetupStepAction): void {
        if (action === 'career-map') {
            this.OnUploadCv();
            return;
        }
        // My profile's `DefaultView` is 'me' (ra-module-definitions.ts), the member's own research areas.
        this.host.NavigateTo({ ModuleKey: RA_MODULE_KEYS.Profile, View: 'me' });
    }

    /** The caught-up panel's "Browse grants" (P/proto-states.js:95): Grant Finder's search view. */
    OnBrowseGrants(): void {
        this.host.NavigateTo({ ModuleKey: RA_MODULE_KEYS.Grants, View: 'search' });
    }

    /** "Widen research areas" (P/proto-states.js:95): the member's profile, where the research areas show. */
    OnWidenResearchAreas(): void {
        this.host.NavigateTo({ ModuleKey: RA_MODULE_KEYS.Profile, View: 'me' });
    }

    /** "Review saved" → Saved (proto-states.js:88). "Continue: {role}" opens the Career Explorer paths. */
    OnSinceAction(action: SinceAction['Action']): void {
        switch (action) {
            case 'saved': {
                const module = this.Modules.find((m) => m.Key === RA_MODULE_KEYS.Saved);
                this.host.NavigateTo({ ModuleKey: RA_MODULE_KEYS.Saved, View: module?.DefaultView ?? SAVED_FALLBACK_VIEW });
                return;
            }
            case 'continue':
                this.host.NavigateTo({ ModuleKey: RA_MODULE_KEYS.CareerMap, View: 'paths' });
                return;
        }
    }

    // ── CV callout ───────────────────────────────────────────────────────

    OnUploadCv(): void {
        const module = this.Modules.find((m) => m.Key === RA_MODULE_KEYS.CareerMap);
        this.host.NavigateTo(module ? { ModuleKey: module.Key, View: module.DefaultView } : CAREER_MAP_FALLBACK_LOCATION);
    }

    // ── This week's picks ────────────────────────────────────────────────

    OnOpenPick(row: HomePickRow): void {
        const target = PICK_OPEN_TARGET[row.kind];
        // Home is the crumb's origin: the detail reads "Back to Home" and returns here (PD §6).
        this.host.NavigateTo({ ModuleKey: target.moduleKey, View: target.view, RecordID: row.key, From: this.host.CurrentLocation });
    }

    /** A tap on a pick row itself, not on one of its controls, opens the pick on a phone (P/Member_Hub.html:1011-1019, `.frec`). */
    OnPickRowClick(event: Event, row: HomePickRow): void {
        if (this.viewport?.IsPhone && IsRowTap(event)) this.OnOpenPick(row);
    }

    async OnSavePick(row: HomePickRow, saved: boolean): Promise<void> {
        await this.itemState.Toggle(row.kind, row.key, { saved });
        this.toast?.ShowSaved(saved); // The bookmark toast (P/Member_Hub.html:953-966), once the save has stuck.
    }

    async OnDismissPick(row: HomePickRow): Promise<void> {
        await this.itemState.Dismiss(row.kind, row.key, row.title);
        this.pickRows = this.pickRows.filter((r) => !(r.kind === row.kind && r.key === row.key));
    }

    async Undo(): Promise<void> {
        await this.itemState.Undo();
    }

    private OnItemStateChanged(change: MemberItemStateChange): void {
        const row = this.pickRows.find((r) => r.kind === change.kind && r.key === change.key);
        if (row) {
            row.saved = change.itemState.saved;
            return;
        }
        if (ShouldReloadHomeForChange(change, this.host.CurrentLocation)) {
            // Undo of a dismiss this component already spliced out of
            // `pickRows` — reload so the restored pick reappears, rather than
            // trying to guess where to reinsert it (mirrors
            // GrantFinderPageComponent.OnItemStateChanged).
            void this.LoadHome();
        }
    }
}
